import { effect, reactive, stop } from "@lune-js/core";
import { error, hasOwn, isFunction, warn } from "@lune-js/utils";
//#region src/scheduler.ts
let queued = false;
const queue = [];
/**
* Flushes all deferred microtask jobs sequentially and resets execution locks.
* @internal
*/
function flushJobs() {
	for (const job of queue) job();
	queue.length = 0;
	queued = false;
}
/**
* Defers execution of a callback function to the end of the current microtask cycle.
* Effectively guarantees synchronization after a round of reactive data mutation loops.
* @param fn - Optional function payload to execute asynchronously.
*/
function nextTick(fn) {
	window.queueMicrotask(() => fn?.());
}
/**
* Adds a unique job function to the asynchronous scheduling queue to prevent redundant evaluations.
* Flushes the collection automatically inside a single deferred microtask frame.
* @param job - The subscriber runtime runner function to register.
*/
function queueJob(job) {
	if (!queue.includes(job)) queue.push(job);
	if (!queued) {
		queued = true;
		window.queueMicrotask(flushJobs);
	}
}
//#endregion
//#region src/context.ts
let inOnce = false;
/**
* Binds all function properties on a scope object explicitly to the scope instance.
* Ensures method invocation contexts (`this`) remain stable when called from template bindings or directives.
* @param scope - The state or methods object containing functions to bind.
*/
function bindContextMethods(scope) {
	for (const key of Object.keys(scope)) if (isFunction(scope[key])) scope[key] = scope[key].bind(scope);
}
/**
* Creates a base execution context for template tracking, expressions, and child blocks.
* If a parent context is provided, it inherits properties from it.
* @param parent - Optional parent context to inherit rules and parent state boundaries from.
* @returns A fresh or derived `Context` lifecycle object.
*/
function createContext(parent) {
	const ctx = {
		delimiters: ["{{", "}}"],
		delimitersRE: /\{\{([^]+?)\}\}/g,
		...parent,
		scope: parent ? parent.scope : reactive({}),
		dirs: parent ? parent.dirs : {},
		effects: [],
		blocks: [],
		cleanups: [],
		effect: (fn) => {
			if (inOnce) {
				queueJob(fn);
				return fn;
			}
			const e = effect(fn, { scheduler: () => queueJob(e) });
			ctx.effects.push(e);
			return e;
		}
	};
	return ctx;
}
/**
* Derives a locally scoped child context using prototype-linked fallback logic.
* Reads fallback to parent scopes automatically, while writes target local scope unless overridden.
* Special handling ensures template refs (`$refs`) are localized via prototype linking.
* @param ctx - The current parent context instance.
* @param data - The localized data object to spawn the scope overlay from.
* @returns An isolated, prototype-shadowed child context.
*/
function createScopedContext(ctx, data = {}) {
	const parentScope = ctx.scope;
	const mergedScope = Object.create(parentScope);
	Object.defineProperties(mergedScope, Object.getOwnPropertyDescriptors(data));
	mergedScope.$refs = typeof parentScope.$refs === "object" || parentScope.$refs === null ? Object.create(parentScope.$refs) : parentScope.$refs;
	const reactiveProxy = reactive(new Proxy(mergedScope, { set(target, key, val, receiver) {
		if (receiver === reactiveProxy && !hasOwn(target, key)) return Reflect.set(parentScope, key, val);
		return Reflect.set(target, key, val, receiver);
	} }));
	bindContextMethods(reactiveProxy);
	return {
		...ctx,
		scope: reactiveProxy
	};
}
function setInOnce(bool) {
	inOnce = bool;
}
//#endregion
//#region src/block.ts
var Block = class {
	ctx;
	template;
	key;
	parentCtx;
	isFragment;
	start;
	end;
	constructor(template, parentCtx, walk, isRoot = false) {
		this.isFragment = template instanceof HTMLTemplateElement;
		if (isRoot) this.template = template;
		else if (this.isFragment) this.template = template.content.cloneNode(true);
		else this.template = template.cloneNode(true);
		if (isRoot) this.ctx = parentCtx;
		else {
			this.parentCtx = parentCtx;
			parentCtx.blocks.push(this);
			this.ctx = createContext(parentCtx);
		}
		walk(this.template, this.ctx);
	}
	get el() {
		return this.start ?? this.template;
	}
	insert(parent, anchor = null) {
		if (parent == null) return;
		if (this.isFragment) {
			if (this.start) {
				let node = this.start;
				let next;
				while (node) {
					next = node.nextSibling;
					parent.insertBefore(node, anchor);
					if (node === this.end) break;
					node = next;
				}
			} else {
				this.start = new Text("");
				this.end = new Text("");
				parent.insertBefore(this.end, anchor);
				parent.insertBefore(this.start, this.end);
				parent.insertBefore(this.template, this.end);
			}
		} else parent.insertBefore(this.template, anchor);
	}
	remove() {
		if (this.parentCtx) remove(this.parentCtx.blocks, this);
		if (this.start) {
			const parent = this.start.parentNode;
			if (parent == null) return;
			let node = this.start;
			let next;
			while (node) {
				next = node.nextSibling;
				parent.removeChild(node);
				if (node === this.end) break;
				node = next;
			}
		} else this.template.parentNode?.removeChild(this.template);
		this.teardown();
	}
	/**
	* Cleanup all effects and child blocks
	* Enhanced with better error handling and cleanup callbacks
	*/
	teardown() {
		for (const child of this.ctx.blocks) try {
			child.teardown();
		} catch (e) {
			if (import.meta.env.DEV) error("Failed tear down of child block:", e);
		}
		for (const effect of this.ctx.effects) try {
			stop(effect);
		} catch (e) {
			if (import.meta.env.DEV) error("Failed stop effect:", e);
		}
		for (const cleanup of this.ctx.cleanups) try {
			cleanup();
		} catch (e) {
			if (import.meta.env.DEV) error("Failed cleanup callback:", e);
		}
		this.ctx.blocks.length = 0;
		this.ctx.effects.length = 0;
		this.ctx.cleanups.length = 0;
	}
};
function remove(arr, el) {
	const i = arr.indexOf(el);
	if (i > -1) arr.splice(i, 1);
}
//#endregion
//#region src/expression/globals.ts
/**
* Built-in globals expressions may reach. Anything outside this list (and the bindings registered
* through {@link allowGlobals}) resolves to a `ReferenceError`, so a template can never walk into
* ambient application state or browser APIs by accident.
*/
const ALLOWED_GLOBALS = /* @__PURE__ */ new Set([
	"Array",
	"BigInt",
	"Boolean",
	"Date",
	"Error",
	"Infinity",
	"Intl",
	"JSON",
	"Map",
	"Math",
	"NaN",
	"Number",
	"Object",
	"Promise",
	"RangeError",
	"RegExp",
	"Set",
	"String",
	"Symbol",
	"TypeError",
	"WeakMap",
	"WeakSet",
	"alert",
	"confirm",
	"console",
	"decodeURI",
	"decodeURIComponent",
	"encodeURI",
	"encodeURIComponent",
	"isFinite",
	"isNaN",
	"parseFloat",
	"parseInt",
	"structuredClone"
]);
/**
* Identifiers that are always rejected, even when an application registers them.
* These are the names that hand an expression a way back to dynamic code evaluation,
* the DOM at large, or the network.
*/
const BLOCKED_IDENTIFIERS = /* @__PURE__ */ new Set([
	"Function",
	"WebSocket",
	"Worker",
	"XMLHttpRequest",
	"document",
	"eval",
	"exports",
	"fetch",
	"global",
	"globalThis",
	"importScripts",
	"module",
	"navigator",
	"process",
	"require",
	"self",
	"setInterval",
	"setTimeout",
	"top",
	"window"
]);
/**
* Property keys that are never readable or writable from an expression. `constructor` and
* `prototype` are the classic routes back to the `Function` constructor, and `__proto__`
* allows prototype pollution.
*/
const UNSAFE_KEYS = /* @__PURE__ */ new Set([
	"__proto__",
	"constructor",
	"prototype"
]);
const customGlobals = Object.create(null);
/**
* Registers extra bindings that expressions may reference by name, for applications that rely on
* globals beyond the built-in allow list (a date library, a formatter, and so on).
* @param globals - A map of identifier name to the value the name should resolve to.
* @throws {Error} When a name is on the permanently blocked list.
*/
function allowGlobals(globals) {
	for (const name of Object.keys(globals)) {
		if (BLOCKED_IDENTIFIERS.has(name)) throw new Error(`"${name}" cannot be exposed to expressions.`);
		customGlobals[name] = globals[name];
	}
}
/**
* Checks whether an identifier resolves to a global binding.
* @param name - The identifier being resolved.
* @returns True when the name is registered or is an allowed built-in that exists at runtime.
*/
function hasGlobal(name) {
	return name in customGlobals || ALLOWED_GLOBALS.has(name) && name in globalThis;
}
/**
* Reads a global binding previously matched by {@link hasGlobal}.
* @param name - The identifier being resolved.
* @returns The registered value, or the built-in of that name.
*/
function getGlobal(name) {
	return name in customGlobals ? customGlobals[name] : globalThis[name];
}
//#endregion
//#region src/expression/compiler.ts
/** Raised when an expression is syntactically valid but reaches for something it is not allowed to. */
var UnsafeExpressionError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "UnsafeExpressionError";
	}
};
/**
* Propagated by an optional chain whose base was nullish. It travels up the member and call links
* of the chain and is turned back into `undefined` at the chain root, matching `?.` short circuiting.
*/
const SHORT_CIRCUIT = Symbol("short-circuit");
const NO_ARGS = [];
/**
* Compiles a parsed program into a reusable closure.
* The AST is walked once, here; running the result never touches the AST or the source again.
* @param program - The parsed statement list.
* @returns A function that runs the program against a scope and returns the value it returns.
* @throws {UnsafeExpressionError} When the program references a blocked identifier or property.
*/
function compile(program) {
	const body = compileStatements(program.body);
	return (scope, el) => {
		const rt = {
			scope,
			el,
			frame: null,
			returned: false,
			result: void 0
		};
		body(rt);
		return rt.result;
	};
}
function compileStatements(statements) {
	const compiled = statements.map(compileStatement);
	if (compiled.length === 1) return compiled[0];
	return (rt) => {
		for (let i = 0; i < compiled.length; i++) {
			compiled[i](rt);
			if (rt.returned) return;
		}
	};
}
function compileStatement(statement) {
	switch (statement.type) {
		case "ExpressionStatement": {
			const expression = compileExpression(statement.expression);
			return (rt) => void expression(rt);
		}
		case "ReturnStatement": {
			const argument = statement.argument && compileExpression(statement.argument);
			return (rt) => {
				rt.result = argument ? unwrap(argument(rt)) : void 0;
				rt.returned = true;
			};
		}
		case "IfStatement": {
			const test = compileExpression(statement.test);
			const consequent = compileStatement(statement.consequent);
			const alternate = statement.alternate && compileStatement(statement.alternate);
			return (rt) => {
				if (unwrap(test(rt))) consequent(rt);
				else if (alternate) alternate(rt);
			};
		}
		case "ThrowStatement": {
			const argument = compileExpression(statement.argument);
			return (rt) => {
				throw unwrap(argument(rt));
			};
		}
		case "BlockStatement": return compileStatements(statement.body);
		case "VariableDeclaration": {
			const declarations = statement.declarations.map(({ name, init }) => {
				assertAllowedIdentifier(name);
				return {
					name,
					init: init && compileExpression(init)
				};
			});
			return (rt) => {
				const frame = rt.frame ??= {
					vars: Object.create(null),
					parent: null
				};
				for (const { name, init } of declarations) frame.vars[name] = init ? unwrap(init(rt)) : void 0;
			};
		}
		case "EmptyStatement": return () => {};
	}
}
function compileExpression(node) {
	switch (node.type) {
		case "Literal": {
			const { value } = node;
			return () => value;
		}
		case "Identifier": return compileIdentifier(node.name);
		case "ThisExpression": return (rt) => rt.scope;
		case "ChainExpression": {
			const expression = compileExpression(node.expression);
			return (rt) => unwrap(expression(rt));
		}
		case "TemplateLiteral": return compileTemplate(node.quasis, node.expressions);
		case "ArrayExpression": return compileArray(node.elements);
		case "ObjectExpression": return compileObject(node.properties);
		case "MemberExpression": return compileMember(node);
		case "CallExpression": return compileCall(node);
		case "NewExpression": {
			const callee = compileExpression(node.callee);
			const args = compileArguments(node.args);
			return (rt) => {
				const target = unwrap(callee(rt));
				if (typeof target !== "function") throw new TypeError(`${describe(node.callee)} is not a constructor`);
				return Reflect.construct(target, args(rt));
			};
		}
		case "UnaryExpression": return compileUnary(node.operator, node.argument);
		case "UpdateExpression": return compileUpdate(node);
		case "BinaryExpression": return compileBinary(node.operator, node.left, node.right);
		case "LogicalExpression": return compileLogical(node.operator, node.left, node.right);
		case "ConditionalExpression": {
			const test = compileExpression(node.test);
			const consequent = compileExpression(node.consequent);
			const alternate = compileExpression(node.alternate);
			return (rt) => unwrap(test(rt)) ? unwrap(consequent(rt)) : unwrap(alternate(rt));
		}
		case "AssignmentExpression": return compileAssignment(node);
		case "ArrowFunctionExpression": return compileArrow(node.params, node.body);
	}
}
/**
* Resolves a bare identifier the way the previous `with(scope)` block did: locals first, then the
* reactive scope (which keeps property reads tracked), then the element and scope aliases, and
* finally the allowed globals.
*/
function compileIdentifier(name) {
	assertAllowedIdentifier(name);
	return (rt) => {
		for (let frame = rt.frame; frame; frame = frame.parent) if (name in frame.vars) return frame.vars[name];
		const { scope } = rt;
		if (scope != null && name in scope) return scope[name];
		if (name === "$data") return scope;
		if (name === "$el") return rt.el;
		if (hasGlobal(name)) return getGlobal(name);
		throw new ReferenceError(`${name} is not defined`);
	};
}
function compileTemplate(quasis, expressions) {
	const compiled = expressions.map(compileExpression);
	return (rt) => {
		let result = quasis[0] ?? "";
		for (let i = 0; i < compiled.length; i++) result += String(unwrap(compiled[i](rt))) + (quasis[i + 1] ?? "");
		return result;
	};
}
function compileArray(elements) {
	const compiled = elements.map((element) => element && {
		spread: element.type === "SpreadElement",
		value: compileExpression(elementValue(element))
	});
	return (rt) => {
		const result = [];
		for (const element of compiled) if (!element) result.length++;
		else if (element.spread) result.push(...unwrap(element.value(rt)));
		else result.push(unwrap(element.value(rt)));
		return result;
	};
}
function compileObject(properties) {
	const compiled = properties.map((property) => {
		if (property.key === null) return {
			spread: true,
			value: compileExpression(property.value)
		};
		if (!property.computed && property.key.type === "Literal" && property.key.value === "__proto__") throw new UnsafeExpressionError("\"__proto__\" cannot be set from an expression.");
		return {
			spread: false,
			key: compileExpression(property.key),
			value: compileExpression(property.value)
		};
	});
	return (rt) => {
		const result = {};
		for (const property of compiled) if (property.spread) Object.assign(result, unwrap(property.value(rt)));
		else {
			const key = unwrap(property.key(rt));
			assertSafeKey(key);
			result[key] = unwrap(property.value(rt));
		}
		return result;
	};
}
function compileMember(node) {
	const object = compileExpression(node.object);
	const optional = node.optional;
	if (!node.computed) {
		const key = node.property.value;
		assertSafeKey(key);
		return (rt) => {
			const target = object(rt);
			if (target === SHORT_CIRCUIT) return SHORT_CIRCUIT;
			if (target == null) {
				if (optional) return SHORT_CIRCUIT;
				throw new TypeError(`Cannot read "${key}" of ${target}`);
			}
			return target[key];
		};
	}
	const property = compileExpression(node.property);
	return (rt) => {
		const target = object(rt);
		if (target === SHORT_CIRCUIT) return SHORT_CIRCUIT;
		const key = unwrap(property(rt));
		if (target == null) {
			if (optional) return SHORT_CIRCUIT;
			throw new TypeError(`Cannot read "${String(key)}" of ${target}`);
		}
		assertSafeKey(key);
		return target[key];
	};
}
/**
* Compiles a call, preserving the receiver rules of the previous implementation: a method reached
* through a member expression is called on its object, and a bare scope member is called on the
* scope, so `this` keeps working inside scope methods.
*/
function compileCall(node) {
	const args = compileArguments(node.args);
	const optional = node.optional;
	const label = describe(node.callee);
	if (node.callee.type === "MemberExpression") {
		const objectNode = node.callee;
		const object = compileExpression(objectNode.object);
		if (!objectNode.computed) assertSafeKey(objectNode.property.value);
		const property = compileExpression(objectNode.property);
		const memberOptional = objectNode.optional;
		return (rt) => {
			const target = object(rt);
			if (target === SHORT_CIRCUIT) return SHORT_CIRCUIT;
			if (target == null) {
				if (memberOptional) return SHORT_CIRCUIT;
				throw new TypeError(`Cannot read "${label}" of ${target}`);
			}
			const key = unwrap(property(rt));
			assertSafeKey(key);
			const fn = target[key];
			if (fn == null && optional) return SHORT_CIRCUIT;
			return invoke(fn, target, args(rt), label);
		};
	}
	if (node.callee.type === "Identifier") {
		const name = node.callee.name;
		assertAllowedIdentifier(name);
		return (rt) => {
			for (let frame = rt.frame; frame; frame = frame.parent) if (name in frame.vars) return invoke(frame.vars[name], void 0, args(rt), name, optional);
			const { scope } = rt;
			if (scope != null && name in scope) return invoke(scope[name], scope, args(rt), name, optional);
			if (hasGlobal(name)) return invoke(getGlobal(name), globalThis, args(rt), name, optional);
			throw new ReferenceError(`${name} is not defined`);
		};
	}
	const callee = compileExpression(node.callee);
	return (rt) => {
		const fn = callee(rt);
		if (fn === SHORT_CIRCUIT) return SHORT_CIRCUIT;
		return invoke(fn, void 0, args(rt), label, optional);
	};
}
function compileArguments(nodes) {
	if (nodes.length === 0) return () => NO_ARGS;
	const compiled = nodes.map((node) => ({
		spread: node.type === "SpreadElement",
		value: compileExpression(elementValue(node))
	}));
	if (compiled.every((argument) => !argument.spread)) return (rt) => compiled.map((argument) => unwrap(argument.value(rt)));
	return (rt) => {
		const args = [];
		for (const argument of compiled) if (argument.spread) args.push(...unwrap(argument.value(rt)));
		else args.push(unwrap(argument.value(rt)));
		return args;
	};
}
function compileUnary(operator, argument) {
	if (operator === "delete") throw new UnsafeExpressionError("The `delete` operator is not allowed in expressions.");
	if (operator === "typeof" && argument.type === "Identifier") {
		const identifier = compileIdentifier(argument.name);
		return (rt) => {
			try {
				return typeof unwrap(identifier(rt));
			} catch (e) {
				if (e instanceof ReferenceError) return "undefined";
				throw e;
			}
		};
	}
	const value = compileExpression(argument);
	switch (operator) {
		case "!": return (rt) => !unwrap(value(rt));
		case "-": return (rt) => -unwrap(value(rt));
		case "+": return (rt) => +unwrap(value(rt));
		case "~": return (rt) => ~unwrap(value(rt));
		case "typeof": return (rt) => typeof unwrap(value(rt));
		case "void": return (rt) => void unwrap(value(rt));
		default: throw new UnsafeExpressionError(`Unsupported unary operator "${operator}".`);
	}
}
function compileUpdate(node) {
	const read = compileExpression(node.argument);
	const write = compileAssigner(node.argument);
	const delta = node.operator === "++" ? 1 : -1;
	const { prefix } = node;
	return (rt) => {
		const previous = Number(unwrap(read(rt)));
		const next = previous + delta;
		write(rt, next);
		return prefix ? next : previous;
	};
}
function compileBinary(operator, leftNode, rightNode) {
	const left = compileExpression(leftNode);
	const right = compileExpression(rightNode);
	const l = (rt) => unwrap(left(rt));
	const r = (rt) => unwrap(right(rt));
	switch (operator) {
		case "+": return (rt) => l(rt) + r(rt);
		case "-": return (rt) => l(rt) - r(rt);
		case "*": return (rt) => l(rt) * r(rt);
		case "/": return (rt) => l(rt) / r(rt);
		case "%": return (rt) => l(rt) % r(rt);
		case "**": return (rt) => l(rt) ** r(rt);
		case "==": return (rt) => l(rt) == r(rt);
		case "!=": return (rt) => l(rt) != r(rt);
		case "===": return (rt) => l(rt) === r(rt);
		case "!==": return (rt) => l(rt) !== r(rt);
		case "<": return (rt) => l(rt) < r(rt);
		case ">": return (rt) => l(rt) > r(rt);
		case "<=": return (rt) => l(rt) <= r(rt);
		case ">=": return (rt) => l(rt) >= r(rt);
		case "&": return (rt) => l(rt) & r(rt);
		case "|": return (rt) => l(rt) | r(rt);
		case "^": return (rt) => l(rt) ^ r(rt);
		case "<<": return (rt) => l(rt) << r(rt);
		case ">>": return (rt) => l(rt) >> r(rt);
		case ">>>": return (rt) => l(rt) >>> r(rt);
		case "in": return (rt) => l(rt) in r(rt);
		case "instanceof": return (rt) => l(rt) instanceof r(rt);
		default: throw new UnsafeExpressionError(`Unsupported operator "${operator}".`);
	}
}
function compileLogical(operator, leftNode, rightNode) {
	const left = compileExpression(leftNode);
	const right = compileExpression(rightNode);
	switch (operator) {
		case "&&": return (rt) => {
			const value = unwrap(left(rt));
			return value ? unwrap(right(rt)) : value;
		};
		case "||": return (rt) => {
			const value = unwrap(left(rt));
			return value ? value : unwrap(right(rt));
		};
		default: return (rt) => {
			return unwrap(left(rt)) ?? unwrap(right(rt));
		};
	}
}
function compileAssignment(node) {
	const write = compileAssigner(node.left);
	const right = compileExpression(node.right);
	const { operator } = node;
	if (operator === "=") return (rt) => {
		const value = unwrap(right(rt));
		write(rt, value);
		return value;
	};
	const read = compileExpression(node.left);
	if (operator === "&&=" || operator === "||=" || operator === "??=") return (rt) => {
		const current = unwrap(read(rt));
		if (!(operator === "&&=" ? Boolean(current) : operator === "||=" ? !current : current === null || current === void 0)) return current;
		const value = unwrap(right(rt));
		write(rt, value);
		return value;
	};
	const apply = compileBinaryOperation(operator.slice(0, -1));
	return (rt) => {
		const value = apply(unwrap(read(rt)), unwrap(right(rt)));
		write(rt, value);
		return value;
	};
}
/** Builds the arithmetic half of a compound assignment such as `+=`. */
function compileBinaryOperation(operator) {
	switch (operator) {
		case "+": return (left, right) => left + right;
		case "-": return (left, right) => left - right;
		case "*": return (left, right) => left * right;
		case "/": return (left, right) => left / right;
		case "%": return (left, right) => left % right;
		case "**": return (left, right) => left ** right;
		case "&": return (left, right) => left & right;
		case "|": return (left, right) => left | right;
		case "^": return (left, right) => left ^ right;
		case "<<": return (left, right) => left << right;
		case ">>": return (left, right) => left >> right;
		case ">>>": return (left, right) => left >>> right;
		default: throw new UnsafeExpressionError(`Unsupported assignment operator "${operator}=".`);
	}
}
/**
* Compiles the write half of an assignment target. Writes to a bare name go to the scope, which
* lets the scoped context proxy forward them to a parent scope when the property is inherited.
*/
function compileAssigner(target) {
	if (target.type === "Identifier") {
		const name = target.name;
		assertAllowedIdentifier(name);
		return (rt, value) => {
			for (let frame = rt.frame; frame; frame = frame.parent) if (name in frame.vars) {
				frame.vars[name] = value;
				return;
			}
			rt.scope[name] = value;
		};
	}
	if (target.type === "MemberExpression") {
		const object = compileExpression(target.object);
		const property = compileExpression(target.property);
		return (rt, value) => {
			const receiver = unwrap(object(rt));
			if (receiver == null) throw new TypeError(`Cannot assign to a property of ${receiver}`);
			const key = unwrap(property(rt));
			assertSafeKey(key);
			receiver[key] = value;
		};
	}
	throw new UnsafeExpressionError("Invalid assignment target.");
}
function compileArrow(params, body) {
	params.forEach(assertAllowedIdentifier);
	const isBlock = Array.isArray(body);
	const run = isBlock ? compileStatements(body) : compileExpression(body);
	return (rt) => {
		const { scope, el, frame: closure } = rt;
		return (...args) => {
			const vars = Object.create(null);
			for (let i = 0; i < params.length; i++) vars[params[i]] = args[i];
			const child = {
				scope,
				el,
				frame: {
					vars,
					parent: closure
				},
				returned: false,
				result: void 0
			};
			if (!isBlock) return unwrap(run(child));
			run(child);
			return child.result;
		};
	};
}
function invoke(fn, thisArg, args, label, optional = false) {
	if (fn == null && optional) return SHORT_CIRCUIT;
	if (typeof fn !== "function") throw new TypeError(`${label} is not a function`);
	return Reflect.apply(fn, thisArg, args);
}
/** Converts a short circuited optional chain back into `undefined` at the point of consumption. */
function unwrap(value) {
	return value === SHORT_CIRCUIT ? void 0 : value;
}
function elementValue(node) {
	return node.type === "SpreadElement" ? node.argument : node;
}
function assertAllowedIdentifier(name) {
	if (BLOCKED_IDENTIFIERS.has(name)) throw new UnsafeExpressionError(`"${name}" is not available to expressions.`);
}
function assertSafeKey(key) {
	if (typeof key === "string" && UNSAFE_KEYS.has(key)) throw new UnsafeExpressionError(`"${key}" cannot be accessed from an expression.`);
}
/** Best effort description of a callee, used for `x is not a function` style messages. */
function describe(node) {
	switch (node.type) {
		case "Identifier": return node.name;
		case "MemberExpression": return node.computed || node.property.type !== "Literal" ? `${describe(node.object)}[...]` : `${describe(node.object)}.${String(node.property.value)}`;
		case "CallExpression": return `${describe(node.callee)}(...)`;
		default: return "expression";
	}
}
//#endregion
//#region src/expression/lexer.ts
const PUNCTUATORS = [
	">>>=",
	"...",
	"===",
	"!==",
	"**=",
	"<<=",
	">>=",
	">>>",
	"&&=",
	"||=",
	"??=",
	"=>",
	"==",
	"!=",
	"<=",
	">=",
	"&&",
	"||",
	"??",
	"?.",
	"++",
	"--",
	"+=",
	"-=",
	"*=",
	"/=",
	"%=",
	"&=",
	"|=",
	"^=",
	"**",
	"<<",
	">>",
	"{",
	"}",
	"(",
	")",
	"[",
	"]",
	".",
	",",
	";",
	":",
	"?",
	"+",
	"-",
	"*",
	"/",
	"%",
	"!",
	"~",
	"<",
	">",
	"=",
	"&",
	"|",
	"^"
];
const ESCAPES = {
	n: "\n",
	t: "	",
	r: "\r",
	b: "\b",
	f: "\f",
	v: "\v",
	"0": "\0"
};
/** Thrown for malformed source; surfaced to the caller as an "Invalid expression" error. */
var ExpressionSyntaxError = class extends SyntaxError {
	position;
	constructor(message, position) {
		super(message);
		this.position = position;
		this.name = "ExpressionSyntaxError";
	}
};
function isIdentifierStart(code) {
	return code >= 97 && code <= 122 || code >= 65 && code <= 90 || code === 36 || code === 95 || code > 127;
}
function isIdentifierPart(code) {
	return isIdentifierStart(code) || code >= 48 && code <= 57;
}
function isDigit(code) {
	return code >= 48 && code <= 57;
}
/**
* Converts an expression source string into a flat token list.
* Comments and whitespace are discarded, but line breaks are recorded on the following token
* so the parser can terminate statements without an explicit semicolon.
* @param source - The raw expression or statement list.
* @returns The tokens, always terminated by a single `eof` token.
* @throws {ExpressionSyntaxError} When the source contains a character sequence that cannot be tokenized.
*/
function tokenize(source) {
	const tokens = [];
	const length = source.length;
	let i = 0;
	let newlineBefore = false;
	while (i < length) {
		const char = source[i];
		const code = source.charCodeAt(i);
		if (code === 10 || code === 13 || code === 8232 || code === 8233) {
			newlineBefore = true;
			i++;
			continue;
		}
		if (code === 32 || code === 9 || code === 11 || code === 12 || code === 160 || code === 65279) {
			i++;
			continue;
		}
		if (char === "/" && source[i + 1] === "/") {
			while (i < length && source.charCodeAt(i) !== 10) i++;
			continue;
		}
		if (char === "/" && source[i + 1] === "*") {
			const end = source.indexOf("*/", i + 2);
			if (end === -1) throw new ExpressionSyntaxError("Unterminated comment", i);
			if (source.slice(i, end).includes("\n")) newlineBefore = true;
			i = end + 2;
			continue;
		}
		const start = i;
		if (isIdentifierStart(code)) {
			i++;
			while (i < length && isIdentifierPart(source.charCodeAt(i))) i++;
			tokens.push({
				type: "name",
				text: source.slice(start, i),
				start,
				newlineBefore
			});
			newlineBefore = false;
			continue;
		}
		if (isDigit(code) || char === "." && isDigit(source.charCodeAt(i + 1))) {
			i = scanNumberEnd(source, i);
			const text = source.slice(start, i);
			const value = Number(text);
			if (Number.isNaN(value)) throw new ExpressionSyntaxError(`Invalid number "${text}"`, start);
			tokens.push({
				type: "num",
				text,
				value,
				start,
				newlineBefore
			});
			newlineBefore = false;
			continue;
		}
		if (char === "\"" || char === "'") {
			const [value, next] = scanString(source, i, char);
			tokens.push({
				type: "str",
				text: source.slice(start, next),
				value,
				start,
				newlineBefore
			});
			i = next;
			newlineBefore = false;
			continue;
		}
		if (char === "`") {
			const [parts, next] = scanTemplate(source, i);
			tokens.push({
				type: "template",
				text: source.slice(start, next),
				parts,
				start,
				newlineBefore
			});
			i = next;
			newlineBefore = false;
			continue;
		}
		const punctuator = PUNCTUATORS.find((p) => source.startsWith(p, i));
		if (!punctuator) throw new ExpressionSyntaxError(`Unexpected character "${char}"`, i);
		i += punctuator.length;
		tokens.push({
			type: "punct",
			text: punctuator,
			start,
			newlineBefore
		});
		newlineBefore = false;
	}
	tokens.push({
		type: "eof",
		text: "",
		start: length,
		newlineBefore
	});
	return tokens;
}
function scanNumberEnd(source, start) {
	let i = start;
	if (source[i] === "0" && /[xXoObB]/.test(source[i + 1] ?? "")) {
		i += 2;
		while (i < source.length && /[0-9a-fA-F]/.test(source[i])) i++;
		return i;
	}
	while (i < source.length && isDigit(source.charCodeAt(i))) i++;
	if (source[i] === ".") {
		i++;
		while (i < source.length && isDigit(source.charCodeAt(i))) i++;
	}
	if (source[i] === "e" || source[i] === "E") {
		let exponent = i + 1;
		if (source[exponent] === "+" || source[exponent] === "-") exponent++;
		if (isDigit(source.charCodeAt(exponent))) {
			i = exponent;
			while (i < source.length && isDigit(source.charCodeAt(i))) i++;
		}
	}
	return i;
}
/**
* Reads a quoted string starting at `start`, decoding escape sequences.
* @returns The decoded value and the offset just past the closing quote.
*/
function scanString(source, start, quote) {
	let value = "";
	let i = start + 1;
	while (i < source.length) {
		const char = source[i];
		if (char === quote) return [value, i + 1];
		if (char === "\\") {
			const [decoded, next] = decodeEscape(source, i);
			value += decoded;
			i = next;
			continue;
		}
		value += char;
		i++;
	}
	throw new ExpressionSyntaxError("Unterminated string", start);
}
function decodeEscape(source, start) {
	const char = source[start + 1];
	if (char === void 0) throw new ExpressionSyntaxError("Unterminated escape sequence", start);
	if (char === "u") {
		if (source[start + 2] === "{") {
			const end = source.indexOf("}", start + 3);
			if (end === -1) throw new ExpressionSyntaxError("Unterminated unicode escape", start);
			return [String.fromCodePoint(parseInt(source.slice(start + 3, end), 16)), end + 1];
		}
		return [String.fromCharCode(parseInt(source.slice(start + 2, start + 6), 16)), start + 6];
	}
	if (char === "x") return [String.fromCharCode(parseInt(source.slice(start + 2, start + 4), 16)), start + 4];
	if (char === "\n") return ["", start + 2];
	return [ESCAPES[char] ?? char, start + 2];
}
/**
* Reads a template literal, keeping each `${}` interpolation as raw source for the parser to
* re-enter recursively. Nested templates, strings and object literals are skipped by depth
* tracking so that braces inside them do not close the interpolation early.
* @returns The split parts and the offset just past the closing backtick.
*/
function scanTemplate(source, start) {
	const quasis = [];
	const expressions = [];
	let current = "";
	let i = start + 1;
	while (i < source.length) {
		const char = source[i];
		if (char === "`") {
			quasis.push(current);
			return [{
				quasis,
				expressions
			}, i + 1];
		}
		if (char === "\\") {
			const [decoded, next] = decodeEscape(source, i);
			current += decoded;
			i = next;
			continue;
		}
		if (char === "$" && source[i + 1] === "{") {
			quasis.push(current);
			current = "";
			const end = scanInterpolationEnd(source, i + 2);
			expressions.push(source.slice(i + 2, end));
			i = end + 1;
			continue;
		}
		current += char;
		i++;
	}
	throw new ExpressionSyntaxError("Unterminated template literal", start);
}
/** Finds the `}` that closes a `${` interpolation, skipping nested braces, strings and templates. */
function scanInterpolationEnd(source, start) {
	let depth = 0;
	let i = start;
	while (i < source.length) {
		const char = source[i];
		if (char === "}" && depth === 0) return i;
		if (char === "{") depth++;
		else if (char === "}") depth--;
		else if (char === "\"" || char === "'") i = scanString(source, i, char)[1] - 1;
		else if (char === "`") i = scanTemplate(source, i)[1] - 1;
		i++;
	}
	throw new ExpressionSyntaxError("Unterminated template interpolation", start);
}
//#endregion
//#region src/expression/parser.ts
const BINARY_PRECEDENCE = {
	"??": 1,
	"||": 2,
	"&&": 3,
	"|": 4,
	"^": 5,
	"&": 6,
	"==": 7,
	"!=": 7,
	"===": 7,
	"!==": 7,
	"<": 8,
	">": 8,
	"<=": 8,
	">=": 8,
	in: 8,
	instanceof: 8,
	"<<": 9,
	">>": 9,
	">>>": 9,
	"+": 10,
	"-": 10,
	"*": 11,
	"/": 11,
	"%": 11,
	"**": 12
};
const LOGICAL_OPERATORS = /* @__PURE__ */ new Set([
	"&&",
	"||",
	"??"
]);
const UNARY_OPERATORS = /* @__PURE__ */ new Set([
	"-",
	"+",
	"!",
	"~",
	"typeof",
	"void",
	"delete"
]);
const ASSIGNMENT_OPERATORS = /* @__PURE__ */ new Set([
	"=",
	"+=",
	"-=",
	"*=",
	"/=",
	"%=",
	"**=",
	"<<=",
	">>=",
	">>>=",
	"&=",
	"|=",
	"^=",
	"&&=",
	"||=",
	"??="
]);
const UNSUPPORTED_KEYWORDS = /* @__PURE__ */ new Set([
	"async",
	"await",
	"break",
	"case",
	"catch",
	"class",
	"continue",
	"debugger",
	"default",
	"do",
	"else",
	"export",
	"extends",
	"finally",
	"for",
	"function",
	"import",
	"super",
	"switch",
	"try",
	"while",
	"with",
	"yield"
]);
const DECLARATION_KEYWORDS = /* @__PURE__ */ new Set([
	"const",
	"let",
	"var"
]);
/**
* Recursive descent parser for the expression subset supported by template bindings.
* Instances are single use; {@link parse} and {@link parseExpressionSource} drive the whole flow.
*/
var Parser = class {
	source;
	tokens;
	index = 0;
	constructor(source) {
		this.source = source;
		this.tokens = tokenize(source);
	}
	parseProgram() {
		const body = [];
		while (!this.isType("eof")) body.push(this.parseStatement());
		return {
			type: "Program",
			body
		};
	}
	parseStatement() {
		const token = this.peek();
		if (token.type === "punct") {
			if (token.text === ";") {
				this.next();
				return { type: "EmptyStatement" };
			}
			if (token.text === "{") return this.parseBlock();
		}
		if (token.type === "name") switch (token.text) {
			case "return": {
				this.next();
				const argument = this.canEndStatement() ? null : this.parseExpression();
				this.consumeTerminator();
				return {
					type: "ReturnStatement",
					argument
				};
			}
			case "if": return this.parseIf();
			case "throw": {
				this.next();
				const argument = this.parseExpression();
				this.consumeTerminator();
				return {
					type: "ThrowStatement",
					argument
				};
			}
			default: if (DECLARATION_KEYWORDS.has(token.text)) return this.parseVariableDeclaration();
		}
		const expression = this.parseExpression();
		this.consumeTerminator();
		return {
			type: "ExpressionStatement",
			expression
		};
	}
	parseBlock() {
		this.expect("{");
		const body = [];
		while (!this.isPunct("}")) {
			if (this.isType("eof")) throw this.error("Unexpected end of expression, expected `}`");
			body.push(this.parseStatement());
		}
		this.next();
		return {
			type: "BlockStatement",
			body
		};
	}
	parseIf() {
		this.next();
		this.expect("(");
		const test = this.parseExpression();
		this.expect(")");
		const consequent = this.parseStatement();
		let alternate = null;
		if (this.isName("else")) {
			this.next();
			alternate = this.parseStatement();
		}
		return {
			type: "IfStatement",
			test,
			consequent,
			alternate
		};
	}
	parseVariableDeclaration() {
		this.next();
		const declarations = [];
		do {
			const name = this.parseIdentifierName();
			let init = null;
			if (this.isPunct("=")) {
				this.next();
				init = this.parseAssignment();
			}
			declarations.push({
				name,
				init
			});
		} while (this.consumeIf(","));
		this.consumeTerminator();
		return {
			type: "VariableDeclaration",
			declarations
		};
	}
	/** Accepts `;`, a closing brace, end of input or a preceding line break as a statement end. */
	consumeTerminator() {
		if (this.consumeIf(";")) return;
		if (this.canEndStatement()) return;
		throw this.error(`Unexpected token "${this.peek().text}"`);
	}
	canEndStatement() {
		const token = this.peek();
		if (token.type === "eof" || token.newlineBefore) return true;
		return token.type === "punct" && (token.text === "}" || token.text === ";");
	}
	parseExpression() {
		return this.parseAssignment();
	}
	parseAssignment() {
		if (this.isArrowAhead()) return this.parseArrow();
		const left = this.parseConditional();
		const token = this.peek();
		if (token.type === "punct" && ASSIGNMENT_OPERATORS.has(token.text)) {
			if (left.type !== "Identifier" && left.type !== "MemberExpression") throw this.error("Invalid assignment target");
			this.next();
			const right = this.parseAssignment();
			return {
				type: "AssignmentExpression",
				operator: token.text,
				left,
				right
			};
		}
		return left;
	}
	parseConditional() {
		const test = this.parseBinary(0);
		if (!this.isPunct("?")) return test;
		this.next();
		const consequent = this.parseAssignment();
		this.expect(":");
		return {
			type: "ConditionalExpression",
			test,
			consequent,
			alternate: this.parseAssignment()
		};
	}
	parseBinary(minPrecedence) {
		let left = this.parseUnary();
		for (;;) {
			const token = this.peek();
			const operator = token.text;
			if (!((token.type === "punct" || token.type === "name" && (operator === "in" || operator === "instanceof")) && operator in BINARY_PRECEDENCE)) break;
			const precedence = BINARY_PRECEDENCE[operator];
			if (precedence < minPrecedence) break;
			this.next();
			const right = this.parseBinary(operator === "**" ? precedence : precedence + 1);
			left = LOGICAL_OPERATORS.has(operator) ? {
				type: "LogicalExpression",
				operator,
				left,
				right
			} : {
				type: "BinaryExpression",
				operator,
				left,
				right
			};
		}
		return left;
	}
	parseUnary() {
		const token = this.peek();
		if ((token.type === "punct" || token.type === "name") && UNARY_OPERATORS.has(token.text)) {
			this.next();
			return {
				type: "UnaryExpression",
				operator: token.text,
				argument: this.parseUnary()
			};
		}
		if (token.type === "punct" && (token.text === "++" || token.text === "--")) {
			this.next();
			const argument = this.parseUnary();
			this.assertUpdateTarget(argument);
			return {
				type: "UpdateExpression",
				operator: token.text,
				argument,
				prefix: true
			};
		}
		const expression = this.parseCallOrMember(this.parsePrimary(), true);
		const next = this.peek();
		if (next.type === "punct" && (next.text === "++" || next.text === "--") && !next.newlineBefore) {
			this.next();
			this.assertUpdateTarget(expression);
			return {
				type: "UpdateExpression",
				operator: next.text,
				argument: expression,
				prefix: false
			};
		}
		return expression;
	}
	assertUpdateTarget(node) {
		if (node.type !== "Identifier" && node.type !== "MemberExpression") throw this.error("Invalid increment or decrement target");
	}
	/**
	* Consumes the member, index and call suffixes that can follow a primary expression.
	* @param allowCall - False while parsing a `new` callee, where the argument list belongs to `new`.
	*/
	parseCallOrMember(object, allowCall) {
		let optionalChain = false;
		for (;;) if (this.isPunct(".")) {
			this.next();
			object = this.staticMember(object, this.parseIdentifierName(), false);
		} else if (this.isPunct("?.")) {
			this.next();
			optionalChain = true;
			if (this.isPunct("(")) {
				if (!allowCall) throw this.error("Optional call is not allowed in a `new` expression");
				object = {
					type: "CallExpression",
					callee: object,
					args: this.parseArguments(),
					optional: true
				};
			} else if (this.isPunct("[")) object = this.computedMember(object, true);
			else object = this.staticMember(object, this.parseIdentifierName(), true);
		} else if (this.isPunct("[")) object = this.computedMember(object, false);
		else if (allowCall && this.isPunct("(")) object = {
			type: "CallExpression",
			callee: object,
			args: this.parseArguments(),
			optional: false
		};
		else return optionalChain ? {
			type: "ChainExpression",
			expression: object
		} : object;
	}
	staticMember(object, name, optional) {
		return {
			type: "MemberExpression",
			object,
			property: {
				type: "Literal",
				value: name
			},
			computed: false,
			optional
		};
	}
	computedMember(object, optional) {
		this.expect("[");
		const property = this.parseExpression();
		this.expect("]");
		return {
			type: "MemberExpression",
			object,
			property,
			computed: true,
			optional
		};
	}
	parseArguments() {
		this.expect("(");
		const args = [];
		while (!this.isPunct(")")) {
			args.push(this.parseSpreadable());
			if (!this.consumeIf(",")) break;
		}
		this.expect(")");
		return args;
	}
	parseSpreadable() {
		if (this.isPunct("...")) {
			this.next();
			return {
				type: "SpreadElement",
				argument: this.parseAssignment()
			};
		}
		return this.parseAssignment();
	}
	parsePrimary() {
		const token = this.peek();
		switch (token.type) {
			case "num":
			case "str":
				this.next();
				return {
					type: "Literal",
					value: token.value
				};
			case "template": {
				this.next();
				const { quasis, expressions } = token.parts;
				return {
					type: "TemplateLiteral",
					quasis,
					expressions: expressions.map((source) => parseExpressionSource(source))
				};
			}
			case "name": return this.parseNamePrimary(token);
			case "punct":
				if (token.text === "(") {
					this.next();
					const expression = this.parseExpression();
					this.expect(")");
					return expression;
				}
				if (token.text === "[") return this.parseArray();
				if (token.text === "{") return this.parseObject();
		}
		throw this.error(`Unexpected token "${token.text || "end of expression"}"`);
	}
	parseNamePrimary(token) {
		const name = token.text;
		if (UNSUPPORTED_KEYWORDS.has(name)) throw this.error(`Unsupported keyword "${name}"`);
		this.next();
		switch (name) {
			case "true": return {
				type: "Literal",
				value: true
			};
			case "false": return {
				type: "Literal",
				value: false
			};
			case "null": return {
				type: "Literal",
				value: null
			};
			case "undefined": return {
				type: "Literal",
				value: void 0
			};
			case "this": return { type: "ThisExpression" };
			case "new": return {
				type: "NewExpression",
				callee: this.parseCallOrMember(this.parsePrimary(), false),
				args: this.isPunct("(") ? this.parseArguments() : []
			};
			default: return {
				type: "Identifier",
				name
			};
		}
	}
	parseArray() {
		this.expect("[");
		const elements = [];
		while (!this.isPunct("]")) {
			if (this.isPunct(",")) {
				this.next();
				elements.push(null);
				continue;
			}
			elements.push(this.parseSpreadable());
			if (!this.consumeIf(",")) break;
		}
		this.expect("]");
		return {
			type: "ArrayExpression",
			elements
		};
	}
	parseObject() {
		this.expect("{");
		const properties = [];
		while (!this.isPunct("}")) {
			if (this.isPunct("...")) {
				this.next();
				properties.push({
					key: null,
					computed: false,
					value: this.parseAssignment()
				});
			} else if (this.isPunct("[")) {
				this.next();
				const key = this.parseExpression();
				this.expect("]");
				this.expect(":");
				properties.push({
					key,
					computed: true,
					value: this.parseAssignment()
				});
			} else {
				const token = this.peek();
				let key;
				if (token.type === "str" || token.type === "num") {
					this.next();
					key = {
						type: "Literal",
						value: String(token.value)
					};
				} else key = {
					type: "Literal",
					value: this.parseIdentifierName()
				};
				if (this.consumeIf(":")) properties.push({
					key,
					computed: false,
					value: this.parseAssignment()
				});
				else if (token.type === "name") properties.push({
					key,
					computed: false,
					value: {
						type: "Identifier",
						name: token.text
					}
				});
				else throw this.error("Expected `:` after a property key");
			}
			if (!this.consumeIf(",")) break;
		}
		this.expect("}");
		return {
			type: "ObjectExpression",
			properties
		};
	}
	parseArrow() {
		const params = [];
		if (this.isPunct("(")) {
			this.next();
			while (!this.isPunct(")")) {
				params.push(this.parseIdentifierName());
				if (!this.consumeIf(",")) break;
			}
			this.expect(")");
		} else params.push(this.parseIdentifierName());
		this.expect("=>");
		if (this.isPunct("{")) return {
			type: "ArrowFunctionExpression",
			params,
			body: this.parseBlock().body
		};
		return {
			type: "ArrowFunctionExpression",
			params,
			body: this.parseAssignment()
		};
	}
	/**
	* Looks ahead for an arrow function head, the only construct that cannot be recognised
	* from its first token alone (`(a, b) =>` versus a parenthesised expression).
	*/
	isArrowAhead() {
		const token = this.peek();
		if (token.type === "name") return this.tokens[this.index + 1]?.text === "=>";
		if (!(token.type === "punct" && token.text === "(")) return false;
		let depth = 0;
		for (let i = this.index; i < this.tokens.length; i++) {
			const current = this.tokens[i];
			if (current.type !== "punct") continue;
			if (current.text === "(" || current.text === "[" || current.text === "{") depth++;
			else if (current.text === ")" || current.text === "]" || current.text === "}") {
				depth--;
				if (depth === 0) return this.tokens[i + 1]?.text === "=>";
			}
		}
		return false;
	}
	parseIdentifierName() {
		const token = this.peek();
		if (token.type !== "name") throw this.error(`Expected an identifier but found "${token.text}"`);
		this.next();
		return token.text;
	}
	peek() {
		return this.tokens[this.index];
	}
	next() {
		return this.tokens[this.index++];
	}
	isType(type) {
		return this.peek().type === type;
	}
	isPunct(text) {
		const token = this.peek();
		return token.type === "punct" && token.text === text;
	}
	isName(text) {
		const token = this.peek();
		return token.type === "name" && token.text === text;
	}
	consumeIf(text) {
		if (!this.isPunct(text)) return false;
		this.next();
		return true;
	}
	expect(text) {
		if (!this.consumeIf(text)) throw this.error(`Expected "${text}" but found "${this.peek().text || "end of expression"}"`);
	}
	error(message) {
		return new ExpressionSyntaxError(`${message} in "${this.source}"`, this.peek().start);
	}
	/** Asserts that every token has been consumed, guarding against trailing garbage. */
	assertConsumed() {
		if (!this.isType("eof")) throw this.error(`Unexpected token "${this.peek().text}"`);
	}
};
/**
* Parses a statement list into an AST.
* @param source - The raw expression source, which may hold several statements.
* @returns The parsed program.
* @throws {ExpressionSyntaxError} When the source falls outside the supported subset.
*/
function parse(source) {
	return new Parser(source).parseProgram();
}
/**
* Parses a single expression, used for template literal interpolations.
* @param source - The raw source of one `${}` interpolation.
* @returns The parsed expression.
* @throws {ExpressionSyntaxError} When the interpolation is empty or malformed.
*/
function parseExpressionSource(source) {
	const parser = new Parser(source);
	const expression = parser.parseExpression();
	parser.assertConsumed();
	return expression;
}
//#endregion
//#region src/eval.ts
/**
* Upper bound on expression source length. Template bindings are short by nature, so anything
* longer is either generated or hostile, and is cheaper to reject than to parse.
*/
const MAX_EXPRESSION_LENGTH = 1e3;
const evalCache = Object.create(null);
/** Stand-in for expressions that failed to compile, so a broken binding is only reported once. */
const noop = () => void 0;
/**
* Evaluates a single expression against a scope and returns its value.
* @param scope - The reactive data object the expression reads from.
* @param exp - The expression source, without a trailing semicolon or statement.
* @param el - The element the binding belongs to, exposed to the expression as `$el`.
* @returns The value the expression produced, or `undefined` when it could not be run.
*/
function evaluate(scope, exp, el) {
	if (!exp.trim()) {
		if (import.meta.env.DEV) warn("Empty expression. `evaluate` must contain an expression.");
		return;
	}
	return execute(scope, `return(${exp})`, el);
}
/**
* Runs a statement list against a scope. Compilation happens once per unique source and is cached;
* every later run reuses the compiled closure tree.
* @param scope - The reactive data object the statements read from and write to.
* @param exp - The source to run, which may contain several statements.
* @param el - The element the binding belongs to, exposed to the expression as `$el`.
* @returns The value returned by the source, or `undefined` when it could not be run.
*/
function execute(scope, exp, el) {
	if (exp.length > MAX_EXPRESSION_LENGTH) {
		if (import.meta.env.DEV) warn(`Expression exceeds the ${MAX_EXPRESSION_LENGTH} character limit and was rejected.`);
		return;
	}
	const fn = evalCache[exp] ?? (evalCache[exp] = toFunction(exp));
	try {
		return fn(scope, el);
	} catch (e) {
		if (import.meta.env.DEV) error(`Failed to execute expression "${exp}":`, e);
		delete evalCache[exp];
		return;
	}
}
/**
* Parses and compiles an expression into a closure tree.
* The engine is a self contained parser and interpreter, so no dynamic code is created and the
* library stays usable under a Content Security Policy that forbids `unsafe-eval`.
* @param exp - The expression source to compile.
* @returns The compiled expression, or a no-op when the source is invalid or unsafe.
*/
function toFunction(exp) {
	try {
		const program = parse(exp);
		if (program.body.length === 0) {
			if (import.meta.env.DEV) warn(`Empty expression. \`execute\` must contain at least one statement.`);
			return noop;
		}
		return compile(program);
	} catch (e) {
		if (import.meta.env.DEV) {
			if (e instanceof UnsafeExpressionError) warn(`Potentially unsafe expression rejected: "${exp}"`, e.message);
			else error(`Invalid expression: "${exp}"`, e);
		}
		return noop;
	}
}
//#endregion
export { Block, allowGlobals, bindContextMethods, createContext, createScopedContext, evaluate, execute, nextTick, queueJob, setInOnce };
